import { forwardRef, HTMLAttributes } from 'react'
import Link from 'next/link'

import Media from '@/components/base/media'
import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'

import { cn } from '@/lib/utils'
import { WPImage, WPLink } from '@/types'

export type Product = {
  color: string
  icon?: WPImage
  image?: WPImage
  title: string
  paragraph: string
  link: WPLink
}

export type ProductCardProps = HTMLAttributes<HTMLDivElement> & Partial<Product>

const ProductCard = forwardRef<HTMLDivElement, ProductCardProps>(
  (
    { color, icon, image, title, paragraph, link, className, ...props },
    ref
  ) => {
    const cardContent = (
      <>
        {icon?.url ? (
          <div className="relative z-10">
            <div
              className="flex w-[12rem] items-center justify-center rounded-radius-minimal transition-transform duration-500 ease-cubic-default group-hover:scale-95"
              style={{ backgroundColor: color }}
            >
              <Media
                image={icon}
                size="full"
                className="h-[8rem] w-[8rem] transition-transform duration-500 ease-cubic-default group-hover:scale-110"
              />
            </div>
          </div>
        ) : null}

        {image ? (
          <div className="relative z-10">
            <div className="flex aspect-square w-[12rem] items-center justify-center overflow-hidden rounded-radius-minimal">
              <Media
                image={image}
                className="h-full w-full object-cover"
                size="medium"
              />
            </div>
          </div>
        ) : null}

        <div className="relative z-10 grid gap-y-spacing-xs">
          {title ? (
            <Typography variant="h6" component="h3">
              {title}
            </Typography>
          ) : null}
          {paragraph ? (
            <Typography variant="regular" paragraph>
              {paragraph}
            </Typography>
          ) : null}
        </div>

        {link?.url && (
          <div className="relative z-10 flex">
            <div className={cn('-m-[1rem] p-[1rem]')}>
              {link?.title ? (
                <span className="sr-only">{link.title}</span>
              ) : null}
              <SVGIcon
                symbol="next-arrow-icon"
                className="h-[.8rem] w-[5.7rem] fill-surface-primary-light transition-transform duration-700 ease-cubic-default group-hover:translate-x-1"
              />
            </div>
          </div>
        )}
      </>
    )

    return (
      <div
        ref={ref}
        className={cn(
          'group relative grid h-full w-full gap-y-spacing-lg rounded-radius-minimal border border-surface-secondary-invert-light px-spacing-sm py-spacing-lg',
          link?.url && '[&_*]:cursor-pointer',
          className
        )}
        {...props}
      >
        {link?.url ? (
          <>
            <span
              data-background
              className="absolute inset-0 z-0 transition-colors duration-500 ease-cubic-default group-hover:bg-surface-secondary-invert-light/20"
            />
            <Link
              href={link.url}
              target={link.target}
              className="absolute inset-0 z-20"
              aria-label={link?.title || title}
              scroll={false}
            >
              {link?.title ? (
                <span className="sr-only">{link.title || title}</span>
              ) : null}
            </Link>
          </>
        ) : null}
        {cardContent}
      </div>
    )
  }
)

export default ProductCard
